/**
 * Hook to integrate data capture into calculator and buying power components.
 * Automatically captures anonymized snapshots when calculations complete.
 */

import { useCallback, useRef } from 'react';
import { dataCaptureService } from '@/services/dataAggregation/DataCaptureService';
import type { MortgageData } from '@/components/calculator/CalculatorTabs';

/** Debounce captures to avoid spamming on every slider change */
const CAPTURE_DEBOUNCE_MS = 3000;

export const useDataCapture = () => {
  const mortgageTimer = useRef<ReturnType<typeof setTimeout>>();
  const buyingPowerTimer = useRef<ReturnType<typeof setTimeout>>();

  const captureMortgageCalculation = useCallback((mortgageData: MortgageData) => {
    if (mortgageTimer.current) clearTimeout(mortgageTimer.current);

    mortgageTimer.current = setTimeout(() => {
      dataCaptureService.captureMortgageCalculator({
        homePrice: mortgageData.homePrice,
        downPayment: mortgageData.downPayment,
        downPaymentPercentage: mortgageData.downPaymentPercentage,
        interestRate: mortgageData.interestRate,
        loanTerm: mortgageData.loanTerm,
        loanType: mortgageData.loanType,
        creditScore: mortgageData.creditScore,
        propertyType: mortgageData.propertyType,
        zipCode: mortgageData.zipCode,
        state: mortgageData.state || mortgageData.location?.state,
        city: mortgageData.city || mortgageData.location?.city,
        isVeteran: mortgageData.isVeteran,
        isFirstTimeHomebuyer: mortgageData.isFirstTimeHomebuyer,
        monthlyPayment: mortgageData.monthlyPayment,
        totalClosingCosts: mortgageData.closingCosts?.total,
      });
    }, CAPTURE_DEBOUNCE_MS);
  }, []);

  const captureBuyingPower = useCallback((data: {
    annualIncome: number;
    totalMonthlyDebts: number;
    dtiRatio: number;
    maxAffordablePrice: number;
    creditScore: number;
    loanType?: string;
    isFirstTimeBuyer?: boolean;
    isVeteran?: boolean;
    hasCoBorrower?: boolean;
    householdSize?: number;
    state?: string;
    propertyType?: string;
  }) => {
    if (buyingPowerTimer.current) clearTimeout(buyingPowerTimer.current);

    buyingPowerTimer.current = setTimeout(() => {
      dataCaptureService.captureBuyingPower(data);
    }, CAPTURE_DEBOUNCE_MS);
  }, []);

  const captureSmartBuyComparison = useCallback((data: {
    scenariosCompared: number;
    homePrice?: number;
    loanType?: string;
    state?: string;
    zipCode?: string;
    creditScore?: number;
  }) => {
    dataCaptureService.captureSmartBuyComparison(data);
  }, []);

  const captureClaraConversation = useCallback((data: {
    readinessScore?: number;
    topicsExplored?: string[];
    state?: string;
    loanType?: string;
    homePrice?: number;
    creditScore?: number;
    isFirstTimeBuyer?: boolean;
  }) => {
    dataCaptureService.captureClaraConversation(data);
  }, []);

  return {
    captureMortgageCalculation,
    captureBuyingPower,
    captureSmartBuyComparison,
    captureClaraConversation,
  };
};
